import { Field, Form, Formik } from 'formik';
import { FrontEdgeLogo } from '../../../../components/icons';
import { SuitUIButton } from '../../../../components/SuitUIButton';
import * as Yup from 'yup';
import { Divider, Select, Spin } from 'antd';
import Input from '../../../../components/input/Input';
import { Bank } from '../../../../customhooks/useBankLogos';
import { LoadingOutlined } from '@ant-design/icons';
import { SignatoryField } from './SignatoryField';
import { useBankAccountLookup } from '../../../../customhooks/useBankAccountLookup';
import { useEffect } from 'react';
import { getBanks, getBank } from 'banks-ng';
import { mutateConfig } from '../../../../api/queryBase';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { getErrorMessage } from '../../../../utils/helper';

export interface SignatureData {
    name?: string;
    file: File | null;
    signatureUrl?: string;
}

export interface ConnectFormValues {
    bank: string;
    name: string;
    code: string;
    accountNumber: string;
    phoneNumber: string;
    accountName: string;
    signatories: SignatureData[];
    bankAddress?: string;
    editingIndex?: number;
}

const Details = ({ onContinue }: { onContinue: () => void }) => {
    const BankResult = getBanks();
    const mutation = mutateConfig({ method: 'post' });

    const validationSchema = Yup.object().shape({
        name: Yup.string().required('Bank name is required'),
        code: Yup.string().required('Bank code is required'),
        accountNumber: Yup.string()
            .required('Account number is required')
            .length(10, 'Please enter a valid 10-digit account number'),
        phoneNumber: Yup.string()
            .required('Phone number is required')
            .matches(/^\d{10}$/, 'Phone number must be 10 digits'),
        editingIndex: Yup.number().notRequired(),
        signatories: Yup.array()
            .of(
                Yup.object().shape({
                    name: Yup.string()
                        .required('Signature name is required')
                        .min(2, 'Name must be at least 2 characters'),
                    signatureUrl: Yup.string().required(
                        'Signature file is required'
                    )
                })
            )
            .min(1, 'At least one signature is required')
    });

    const initialValues: ConnectFormValues = {
        bank: '',
        name: '',
        code: '',
        accountNumber: '',
        phoneNumber: '',
        accountName: '',
        signatories: [],
        editingIndex: undefined
    };

    const { mutate, isLoading } = mutation({
        url: `/banks/connect`
    });

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            validateOnBlur={true}
            onSubmit={(values) => {
                const transformedPayload = {
                    bankAddress: values.bankAddress,
                    entries: [
                        {
                            bankName: values.name,
                            code: values.code,
                            accountName: values.accountName,
                            accountNumber: values.accountNumber,
                            phoneNumber: `+234${values.phoneNumber}`
                        }
                    ],
                    signatories: values.signatories.map((s) => ({
                        name: s.name,
                        signatureUrl: s.signatureUrl
                    })),
                    requesterName: values.signatories[0]?.name || '',
                    requesterTitle: ''
                };

                mutate(transformedPayload, {
                    onSettled: (_, error: AxiosError | undefined | any) => {
                        if (error) {
                            toast.error(
                                getErrorMessage(error) ||
                                    'Connect request failed, Please try again later.'
                            );
                            return;
                        }
                        onContinue();
                    }
                });
            }}
        >
            {({
                values,
                errors,
                touched,
                isValid,
                setFieldValue,
                setFieldError,
                setFieldTouched
            }) => {
                const lookupEnabled = Boolean(
                    values.code && values.accountNumber.length === 10
                );

                const { isFetching, accountName, error } = useBankAccountLookup(
                    {
                        bankCode: values.code,
                        accountNumber: values.accountNumber,
                        enabled: lookupEnabled
                    }
                );

                useEffect(() => {
                    if (error) {
                        setFieldTouched('accountNumber', true, false);
                        setFieldError(
                            'accountNumber',
                            'Couldn’t verify your account. Try again later.'
                        );
                        setFieldValue('accountName', '', false);
                    }

                    if (accountName && values.accountName !== accountName) {
                        setFieldTouched('accountNumber', false, false);
                        setFieldError('accountNumber', '');
                        setFieldValue('accountName', accountName, false);
                    }
                }, [
                    accountName,
                    values.accountName,
                    setFieldValue,
                    setFieldError,
                    error,
                    setFieldTouched
                ]);

                return (
                    <Form className='p-4 pt-6 space-y-6 pb-12 relative'>
                        <FrontEdgeLogo size='120' />

                        <div className='text-center'>
                            <h2 className='font-semibold text-[20px]'>
                                Account Details
                            </h2>
                            <p className='text-midnightHaze text-[16px]'>
                                To complete your request, provide the following
                                details:
                            </p>
                        </div>

                        <div className='mb-6 space-y-4 px-5'>
                            <div>
                                <label
                                    htmlFor='bank'
                                    className='block mb-2 text-sm font-medium text-gray700 text-left'
                                >
                                    Bank name
                                </label>
                                <Select
                                    style={{ borderRadius: '8px' }}
                                    className='w-full h-[45px] rounded-[8px]'
                                    id='bank'
                                    showSearch
                                    onChange={(value) => {
                                        const selectedBank = getBank(value);
                                        setFieldValue(
                                            'code',
                                            selectedBank?.code
                                        );
                                        setFieldValue(
                                            'name',
                                            selectedBank?.name
                                        );
                                        setFieldValue(
                                            'bankAddress',
                                            selectedBank?.address || ''
                                        );
                                        setFieldValue('accountName', '', false);
                                    }}
                                    options={BankResult.map((bank: Bank) => ({
                                        label: (
                                            <div className='flex gap-2 items-center'>
                                                <div className='w-[20px] h-[20px] rounded-full border border-[#D0D5DD] overflow-hidden flex items-center justify-center'>
                                                    <img
                                                        src={bank.logo}
                                                        alt='bank-logo'
                                                        className='object-contain w-full h-full'
                                                    />
                                                </div>
                                                {bank.name}
                                            </div>
                                        ),
                                        value: bank.id,
                                        labelText: bank.name
                                    }))}
                                    filterOption={(input, option) => {
                                        if (!option) return false;
                                        return option.labelText
                                            ?.toLowerCase()
                                            .includes(input.toLowerCase());
                                    }}
                                />
                                {touched.bank && errors.bank && (
                                    <p className='text-xs text-red100'>
                                        {errors.bank}
                                    </p>
                                )}
                            </div>

                            <Field
                                as={Input}
                                isError={
                                    touched?.accountNumber &&
                                    errors?.accountNumber
                                }
                                errorMessage={
                                    touched?.accountNumber &&
                                    errors?.accountNumber
                                }
                                required
                                disabled={isFetching}
                                maxLength={10}
                                type='number'
                                className='w-full'
                                id='connect-accountNumber'
                                label='Account number'
                                name='accountNumber'
                                rightIcon={
                                    isFetching && (
                                        <Spin
                                            indicator={<LoadingOutlined spin />}
                                        />
                                    )
                                }
                                infoText={
                                    values.accountName &&
                                    values.accountNumber.length === 10 &&
                                    !isFetching ? (
                                        <p className='pt-2 text-midnightHaze'>
                                            {values.accountName}
                                        </p>
                                    ) : null
                                }
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                ) => {
                                    const newValue = e.target.value;
                                    setFieldValue('accountNumber', newValue);
                                    if (!values.bank) {
                                        setFieldError('bank', 'Select a bank');
                                    }
                                    if (newValue.length < 10) {
                                        setFieldValue('accountName', '', false);
                                    }
                                }}
                            />

                            <Field
                                as={Input}
                                isError={
                                    touched?.phoneNumber && errors?.phoneNumber
                                }
                                errorMessage={
                                    touched?.phoneNumber && errors?.phoneNumber
                                }
                                leftIcon={
                                    <div className='text-xs ml-[-5px] pr-2'>
                                        +234
                                    </div>
                                }
                                required
                                className='w-full'
                                id='signup-phoneNumber'
                                label='Phone Number'
                                name='phoneNumber'
                            />

                            <Divider
                                dashed
                                className='border-appcolorfaintgreyblue mt-8'
                            />
                            <SignatoryField />
                        </div>

                        <SuitUIButton
                            text='Continue'
                            disabled={!isValid || !!values.editingIndex}
                            fullWidth
                            type='submit'
                            isLoading={isLoading}
                        />
                    </Form>
                );
            }}
        </Formik>
    );
};

export default Details;
